import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Filter, X, Building2, Hash, ArrowLeftRight, RefreshCw, Wand2, HelpCircle, Repeat } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Transactions = ({ token }) => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({ search_term: '', category_id: '', similar_count: 0 });
  const [showHelpTips, setShowHelpTips] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    category_id: '',
    type: '',
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    source_account: ''
  });
  
  const [formData, setFormData] = useState({
    category_id: '',
    amount: '',
    type: 'expense',
    description: '',
    date: new Date().toISOString().split('T')[0],
    source_account: '',
    reference: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.type) params.append('type', filters.type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.amount_min) params.append('amount_min', filters.amount_min);
      if (filters.amount_max) params.append('amount_max', filters.amount_max);
      if (filters.source_account) params.append('source_account', filters.source_account);
      
      const response = await axios.get(`${BACKEND_URL}/api/transactions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(response.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      if (editingTransaction) {
        await axios.put(
          `${BACKEND_URL}/api/transactions/${editingTransaction.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Transaction updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/transactions`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Transaction added successfully');
      }

      setShowModal(false);
      setEditingTransaction(null);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      amount: '',
      type: 'expense',
      description: '',
      date: new Date().toISOString().split('T')[0],
      source_account: '',
      reference: ''
    });
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      category_id: transaction.category_id,
      amount: transaction.amount,
      type: transaction.type,
      description: transaction.description,
      date: transaction.date,
      source_account: transaction.source_account || '',
      reference: transaction.reference || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Transaction deleted successfully');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const handleMarkAsTransfer = async (transaction) => {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/transactions/${transaction.id}/mark-transfer`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} transactions?`)) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/transactions/bulk-delete`,
        selectedIds,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${selectedIds.length} transactions`);
      setSelectedIds([]);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleEditWithBulkOption = async (transaction) => {
    // Check for similar transactions using key parts of description
    try {
      // Try to find a meaningful search term from the description
      // Look for common patterns like merchant names (often appear after bank codes)
      const desc = transaction.description;
      let searchTerm = '';
      
      // If description has "Fresh Frui" or similar merchant pattern, use that
      // Otherwise use first significant word(s)
      const words = desc.split(/\s+/).filter(w => w.length > 2);
      
      // Skip bank codes/prefixes (usually short or contain special chars)
      const meaningfulWords = words.filter(w => 
        w.length > 4 && !/^[A-Z]{2,4}\*/.test(w) && !/^\d+$/.test(w)
      );
      
      if (meaningfulWords.length > 0) {
        // Use first 2-3 meaningful words
        searchTerm = meaningfulWords.slice(0, 2).join(' ');
      } else if (words.length > 0) {
        searchTerm = words[0];
      }
      
      if (!searchTerm || searchTerm.length < 3) {
        // Fallback to first word
        searchTerm = words[0] || desc.substring(0, 10);
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/transactions/find-similar?search_term=${encodeURIComponent(searchTerm)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.count > 1) {
        setBulkUpdateData({
          search_term: searchTerm,
          category_id: '',
          similar_count: response.data.count,
          update_type: '',
          save_as_rule: false
        });
      }
    } catch (error) {
      console.error('Error checking similar transactions:', error);
    }
    
    handleEdit(transaction);
  };

  const handleBulkUpdateSimilar = async () => {
    if (!bulkUpdateData.category_id) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/transactions/bulk-update-with-rule`,
        {
          search_term: bulkUpdateData.search_term,
          category_id: bulkUpdateData.category_id,
          update_type: bulkUpdateData.update_type || null,
          save_as_rule: bulkUpdateData.save_as_rule
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      let message = response.data.message;
      if (response.data.rule_saved) {
        message += ' Rule saved for future imports!';
      }
      toast.success(message);
      setShowBulkUpdateDialog(false);
      setBulkUpdateData({ search_term: '', category_id: '', similar_count: 0, update_type: '', save_as_rule: false });
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update similar transactions');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === transactions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(transactions.map(t => t.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category_id: '',
      type: '',
      date_from: '',
      date_to: '',
      amount_min: '',
      amount_max: '',
      source_account: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const filteredCategories = categories.filter(cat => cat.type === formData.type);

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-body text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
            Transactions
          </h1>
          <p className="mt-1 text-base font-body text-muted-foreground">
            {transactions.length} transactions {hasActiveFilters && '(filtered)'}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedIds.length})
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHelpTips(!showHelpTips)}
            className={showHelpTips ? 'bg-purple-100 text-purple-700' : ''}
            data-testid="toggle-help-button"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? 'border-primary text-primary' : ''}
            data-testid="toggle-filters-button"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v !== '').length})`}
          </Button>
          <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-transaction-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Transaction
          </Button>
        </div>
      </div>

      {/* Help Tips Panel */}
      {showHelpTips && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4" data-testid="help-tips-panel">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-3 text-sm">
              <div>
                <h4 className="font-semibold text-purple-900 mb-1">Quick Tips for Managing Transactions</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white/70 rounded-md p-3 border border-purple-100">
                  <p className="font-medium text-purple-800 mb-1 flex items-center gap-2">
                    <Wand2 className="h-4 w-4" /> Bulk Update Similar
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Click <span className="font-mono bg-purple-100 px-1 rounded">Edit</span> on any transaction. If similar ones exist, you'll see an "Update Similar" button to categorize them all at once.
                  </p>
                </div>
                <div className="bg-white/70 rounded-md p-3 border border-purple-100">
                  <p className="font-medium text-purple-800 mb-1 flex items-center gap-2">
                    <Repeat className="h-4 w-4" /> Exclude from Totals
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Credit card payments? Internal transfers? Click the <span className="font-mono bg-blue-100 px-1 rounded">↔</span> button to mark as transfer—it won't count in income/expense totals.
                  </p>
                </div>
                <div className="bg-white/70 rounded-md p-3 border border-purple-100">
                  <p className="font-medium text-purple-800 mb-1 flex items-center gap-2">
                    <Filter className="h-4 w-4" /> Filter & Search
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Use the search bar to find transactions by description. Click "Filters" for advanced options like date range, amount, or account.
                  </p>
                </div>
                <div className="bg-white/70 rounded-md p-3 border border-purple-100">
                  <p className="font-medium text-purple-800 mb-1 flex items-center gap-2">
                    <Trash2 className="h-4 w-4" /> Bulk Delete
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Select multiple transactions using checkboxes, then click "Delete" to remove them all at once.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground italic">
                Tip: AI categorization learns from your choices. The more you correct, the better it gets!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search transactions..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {showFilters && (
          <div className="bg-card border border-border rounded-sm p-4 space-y-4" data-testid="filters-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Filters</h3>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Clear all
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">Category</Label>
                <Select
                  value={filters.category_id}
                  onValueChange={(value) => setFilters({ ...filters, category_id: value === 'all' ? '' : value })}
                >
                  <SelectTrigger data-testid="filter-category">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select
                  value={filters.type}
                  onValueChange={(value) => setFilters({ ...filters, type: value === 'all' ? '' : value })}
                >
                  <SelectTrigger data-testid="filter-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date From</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <Label className="text-xs">Date To</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  data-testid="filter-date-to"
                />
              </div>
              <div>
                <Label className="text-xs">Min Amount</Label>
                <Input
                  type="number"
                  placeholder="$0"
                  value={filters.amount_min}
                  onChange={(e) => setFilters({ ...filters, amount_min: e.target.value })}
                  data-testid="filter-amount-min"
                />
              </div>
              <div>
                <Label className="text-xs">Max Amount</Label>
                <Input
                  type="number"
                  placeholder="$999999"
                  value={filters.amount_max}
                  onChange={(e) => setFilters({ ...filters, amount_max: e.target.value })}
                  data-testid="filter-amount-max"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Source Account</Label>
                <Input
                  placeholder="Account number or reference"
                  value={filters.source_account}
                  onChange={(e) => setFilters({ ...filters, source_account: e.target.value })}
                  data-testid="filter-source-account"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="transactions-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-2 py-3 text-left">
                  <Checkbox
                    checked={selectedIds.length === transactions.length && transactions.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-2 py-3 text-left text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Account
                </th>
                <th className="px-2 py-3 text-left text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Description
                </th>
                <th className="px-2 py-3 text-left text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Category
                </th>
                <th className="px-2 py-3 text-center text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Type
                </th>
                <th className="px-2 py-3 text-right text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-2 py-3 text-right text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                    {hasActiveFilters ? 'No transactions match your filters' : 'No transactions yet'}
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className={`hover:bg-muted/30 ${selectedIds.includes(transaction.id) ? 'bg-primary/5' : ''} ${transaction.is_transfer || transaction.type === 'transfer' ? 'bg-blue-50/30' : ''}`}
                    data-testid={`transaction-row-${transaction.id}`}
                  >
                    <td className="px-2 py-3">
                      <Checkbox
                        checked={selectedIds.includes(transaction.id)}
                        onCheckedChange={() => toggleSelect(transaction.id)}
                        data-testid={`select-checkbox-${transaction.id}`}
                      />
                    </td>
                    <td className="px-2 py-3">
                      {transaction.source_account && (
                        <div className="text-xs font-mono text-muted-foreground flex items-center gap-1" title={transaction.source_account}>
                          <Building2 className="h-3 w-3" />
                          ...{transaction.source_account.slice(-4)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3 text-sm font-mono text-foreground whitespace-nowrap">
                      {new Date(transaction.date).toLocaleDateString('en-AU')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-body text-foreground max-w-xs truncate">
                        {transaction.description}
                      </div>
                      {transaction.reference && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Hash className="h-3 w-3" />
                          {transaction.reference}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-mono rounded-sm ${
                        transaction.type === 'income'
                          ? 'bg-green-100 text-green-700'
                          : transaction.type === 'transfer' || transaction.is_transfer
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {transaction.category_name}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-center">
                      {(transaction.is_transfer || transaction.type === 'transfer') ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                          <ArrowLeftRight className="h-3 w-3" />
                          Transfer
                        </span>
                      ) : (
                        <span className={`text-xs font-mono ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                          {transaction.type === 'income' ? 'IN' : 'OUT'}
                        </span>
                      )}
                    </td>
                    <td className={`px-2 py-3 text-right text-sm font-mono font-medium ${
                      transaction.type === 'income'
                        ? 'text-green-600'
                        : transaction.type === 'transfer' || transaction.is_transfer
                        ? 'text-blue-600'
                        : 'text-red-600'
                    }`}>
                      {transaction.type === 'income' ? '+' : transaction.type === 'transfer' ? '' : '-'}${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-2 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkAsTransfer(transaction)}
                          title={transaction.is_transfer || transaction.type === 'transfer' ? 'Unmark as transfer' : 'Mark as transfer (exclude from totals)'}
                          className={transaction.is_transfer || transaction.type === 'transfer' ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50' : 'text-muted-foreground hover:text-blue-600 hover:bg-blue-50'}
                          data-testid={`toggle-transfer-${transaction.id}`}
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditWithBulkOption(transaction)}
                          title="Edit (with bulk update option)"
                          data-testid={`edit-transaction-${transaction.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(transaction.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-transaction-${transaction.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Transaction Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) setEditingTransaction(null); }}>
        <DialogContent className="max-w-md" data-testid="transaction-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
            </DialogTitle>
            <DialogDescription className="font-body">
              {editingTransaction ? 'Update the transaction details.' : 'Add a new income or expense transaction.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="type" className="font-body">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value, category_id: '' })}
              >
                <SelectTrigger id="type" data-testid="transaction-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="font-body">Category</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger id="category" data-testid="transaction-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {filteredCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount" className="font-body">Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                data-testid="transaction-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="font-body">Description</Label>
              <Input
                id="description"
                placeholder="What was this transaction for?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                data-testid="transaction-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date" className="font-body">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                data-testid="transaction-date-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source_account" className="font-body">Source Account</Label>
                <Input
                  id="source_account"
                  placeholder="Account #"
                  value={formData.source_account}
                  onChange={(e) => setFormData({ ...formData, source_account: e.target.value })}
                  data-testid="transaction-source-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reference" className="font-body">Reference</Label>
                <Input
                  id="reference"
                  placeholder="Ref #"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  data-testid="transaction-reference-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingTransaction(null); }}>
                Cancel
              </Button>
              {bulkUpdateData.similar_count > 1 && (
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false);
                    setShowBulkUpdateDialog(true);
                  }}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Update {bulkUpdateData.similar_count} Similar
                </Button>
              )}
              <Button type="submit" data-testid="save-transaction-button">
                {editingTransaction ? 'Update' : 'Add'} Transaction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Similar Transactions Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="max-w-md" data-testid="bulk-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Wand2 className="h-6 w-6 text-purple-600" />
              Update Similar Transactions
            </DialogTitle>
            <DialogDescription className="font-body">
              Found <strong>{bulkUpdateData.similar_count}</strong> transactions containing "<strong>{bulkUpdateData.search_term}</strong>". 
              Update them all to the same category?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-body">New Category</Label>
              <Select
                value={bulkUpdateData.category_id}
                onValueChange={(value) => setBulkUpdateData({ ...bulkUpdateData, category_id: value })}
              >
                <SelectTrigger data-testid="bulk-update-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="font-body">Change Type (Optional)</Label>
              <Select
                value={bulkUpdateData.update_type || ''}
                onValueChange={(value) => setBulkUpdateData({ ...bulkUpdateData, update_type: value === 'keep' ? '' : value })}
              >
                <SelectTrigger data-testid="bulk-update-type-select">
                  <SelectValue placeholder="Keep existing type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep existing type</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="transfer">Transfer (excluded from totals)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
              <Checkbox
                id="save-as-rule"
                checked={bulkUpdateData.save_as_rule}
                onCheckedChange={(checked) => setBulkUpdateData({ ...bulkUpdateData, save_as_rule: checked })}
                data-testid="save-as-rule-checkbox"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="save-as-rule"
                  className="text-sm font-medium text-blue-900 cursor-pointer"
                >
                  Save as rule for future imports
                </label>
                <p className="text-xs text-blue-700">
                  Next time you import, transactions with "{bulkUpdateData.search_term}" will be auto-categorized
                </p>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-sm p-3">
              <p className="text-sm text-purple-900">
                <strong>Example:</strong> If you update "{bulkUpdateData.search_term}" → {categories.find(c => c.id === bulkUpdateData.category_id)?.name || 'selected category'}, all {bulkUpdateData.similar_count} transactions 
                with "{bulkUpdateData.search_term}" in the description will be updated.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpdateSimilar} data-testid="confirm-bulk-update-button">
              <RefreshCw className="h-4 w-4 mr-2" />
              Update All {bulkUpdateData.similar_count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
